import { useEffect } from 'react'
import {
  OG_IMAGE_URL,
  SITE_URL,
  upsertLink,
  upsertMeta,
  upsertJsonLd,
  removeJsonLd,
  breadcrumbJsonLd,
  webApplicationJsonLd,
  howToJsonLd,
} from './useSeo'
import { getLandingPagesByTopic, getProgrammaticLandingPagesByTopic } from '../content/landingPages'
import { COST_CLUSTER_PILLAR_TOPIC_ID } from '../content/landingPages/relatedPages'
import type { AnyLandingPageContent } from '../content/landingPages/types'
import { SUPPORTED_LANGUAGES } from '../i18n'

function jsonLdId(slug: string) {
  return `seo-landing-jsonld-${slug}`
}

function breadcrumbId(slug: string) {
  return `seo-landing-breadcrumb-${slug}`
}

function webAppId(slug: string) {
  return `seo-landing-webapp-${slug}`
}

function howToId(slug: string) {
  return `seo-landing-howto-${slug}`
}

/** SEO tags for the /<slug> landing pages in content/landingPages - static-per-page like
 *  useBlogSeo/usePvRechnerSeo, but generalized since all pages share the same tag shape
 *  (title/description/canonical/OG + optional FAQPage schema when the page has a faq section).
 *  Each page also gets hreflang alternates to its DE/ES/PL siblings (same topicId, see
 *  content/landingPages/index.ts), same pattern as the homepage's useSeo.ts.
 *
 *  indexPolicy: 'noindex-canonical-to' points the canonical tag at canonicalTarget instead of the
 *  page's own slug (Duplicate-Content-Vermeidung, siehe Task-01-Canonical-Strategie in
 *  types.ts) - robots stays "index, follow" even then, since noindex would contradict the
 *  canonical signal. indexPolicy: 'noindex' sets robots to "noindex, follow" instead (same
 *  choice as LegalPage.tsx: follow so link equity still flows), always explicitly reset to
 *  "index, follow" on unmount so a later page that doesn't set this hook's indexPolicy at all
 *  never inherits a stale noindex tag from client-side navigation. */
export function useLandingPageSeo(content: AnyLandingPageContent) {
  useEffect(() => {
    document.documentElement.lang = content.lang
    const id = jsonLdId(content.slug)
    const breadcrumbElId = breadcrumbId(content.slug)
    const webAppElId = webAppId(content.slug)
    const howToElId = howToId(content.slug)
    const indexPolicy = content.indexPolicy ?? 'index'
    const canonicalSlug = indexPolicy === 'noindex-canonical-to' && content.canonicalTarget ? content.canonicalTarget : content.slug
    const canonicalUrl = `${SITE_URL}/${canonicalSlug}`

    document.title = content.title
    upsertMeta('name', 'robots', indexPolicy === 'noindex' ? 'noindex, follow' : 'index, follow')
    upsertMeta('name', 'description', content.metaDescription)
    upsertMeta('name', 'keywords', content.keywords.join(', '))
    upsertMeta('property', 'og:title', content.title)
    upsertMeta('property', 'og:description', content.metaDescription)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('property', 'og:image', OG_IMAGE_URL)
    upsertMeta('name', 'twitter:title', content.title)
    upsertMeta('name', 'twitter:description', content.metaDescription)
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL)
    upsertLink('canonical', canonicalUrl)

    const siblings = [...getLandingPagesByTopic(content.topicId), ...getProgrammaticLandingPagesByTopic(content.topicId)]
    for (const lang of SUPPORTED_LANGUAGES) {
      const sibling = siblings.find((page) => page.lang === lang)
      if (sibling) upsertLink('alternate', `${SITE_URL}/${sibling.slug}`, lang)
    }
    const deVariant = siblings.find((page) => page.lang === 'de')
    if (deVariant) upsertLink('alternate', `${SITE_URL}/${deVariant.slug}`, 'x-default')

    if (content.faq && content.faq.length > 0) {
      upsertJsonLd(id, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: content.faq.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      })
    }

    // pSEO-Story Task 06 (Pillar-Page-Struktur): every programmatic kWp/kWh page rolls up to the
    // same-language "Solaranlage Kosten" overview page - a 3-level breadcrumb (Home > Pillar >
    // Page) makes that cluster relationship explicit to search engines, not just the 2-level
    // Home > Page breadcrumb the 21 static/blog-adjacent pages get. Looked up live via
    // getLandingPagesByTopic instead of stored per-page, so it can never drift out of sync with
    // the actual pillar page's slug/title.
    const pillar = content.calculatorPrefill
      ? getLandingPagesByTopic(COST_CLUSTER_PILLAR_TOPIC_ID).find((page) => page.lang === content.lang)
      : undefined

    upsertJsonLd(
      breadcrumbElId,
      breadcrumbJsonLd([
        { name: 'Roofora', url: `${SITE_URL}/${content.lang}` },
        ...(pillar ? [{ name: pillar.h1, url: `${SITE_URL}/${pillar.slug}` }] : []),
        { name: content.h1, url: canonicalUrl },
      ]),
    )

    // pSEO-Story Task 07 (Structured Data & E-E-A-T): every page embedding the calculator widget
    // (calculatorPrefill set, see Task 02) gets the same WebApplication schema the two bespoke
    // calculator pages (/pv-rechner, /pv-kostenrechner) already had - previously only those two
    // had it, the 39 programmatic pSEO pages had none despite embedding the identical widget.
    if (content.calculatorPrefill) {
      upsertJsonLd(webAppElId, webApplicationJsonLd(content.title, content.metaDescription, canonicalUrl))
    }

    // GitLab-Issues #143/#144 (Sub-Issues von #142): HowTo-Strukturdaten für die beiden
    // Schritt-für-Schritt-Guides - nur gesetzt, wenn die Seite tatsächlich `steps` hat, sonst kein
    // leeres/irreführendes HowTo-Schema auf den übrigen 20+ Landingpages.
    if (content.steps && content.steps.length > 0) {
      upsertJsonLd(howToElId, howToJsonLd(content.h1, content.metaDescription, content.steps))
    }

    return () => {
      removeJsonLd(id)
      removeJsonLd(breadcrumbElId)
      removeJsonLd(webAppElId)
      removeJsonLd(howToElId)
      upsertMeta('name', 'robots', 'index, follow')
    }
  }, [content])
}
